import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill, type PillTone } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import {
  BENCH_ATTACH_LANDS_WITHIN_SECONDS,
  type BenchRunDetail,
  type BenchRunEventView,
  type BenchRunSegmentView,
} from '@siliconx/shared';
import { cancelBenchAttach } from '../../lib/bench';
import { qk } from '../../lib/queryKeys';
import { AgentText } from './AgentText';
import { formatBenchWhen } from './benchTime';

/**
 * SIL-1708. The boots (segments) of a run and its audit events, newest event first. The portal holds no grant on event detail,
 * subject or source IP, so an event shows kind, reason, time and whether you did it, nothing else. A kind this page does not know
 * renders generically (the server may add kinds before the portal learns them). Every server string goes through AgentText.
 */

const SEGMENT_REASON: Record<string, string> = {
  initial: 'First boot',
  handoff: 'Handoff to the next environment',
  repair_attach: 'Recovery (attached after a restart)',
};

const SEGMENT_STATUS_TONE: Record<BenchRunSegmentView['status'], PillTone> = {
  armed: 'amber',
  active: 'green',
  done: 'neutral',
  expired: 'red',
  superseded: 'neutral',
};

function segmentStatusText(s: BenchRunSegmentView): string {
  switch (s.status) {
    case 'armed':
      return s.expiresAt ? `Waiting for the machine to boot until ${formatBenchWhen(s.expiresAt)}` : 'Waiting for the machine to boot';
    case 'active':
      return 'Running';
    case 'done':
      return 'Finished';
    case 'expired':
      return 'Expired before the machine booted';
    case 'superseded':
      return 'Replaced by a newer boot';
    default:
      return 'Unknown state';
  }
}

export const EVENT_LABEL: Record<string, string> = {
  handoff_armed: 'Handoff armed',
  resume_ok: 'Resumed',
  resume_refused: 'Resume refused',
  resume_reuse: 'Resume token presented again',
  reuse_acknowledged: 'Reuse acknowledged',
  attach_approved: 'Attach approved',
  attach_refused: 'Attach refused',
  attach: 'Machine attached (recovery)',
  session_superseded: 'Session replaced',
  segment_expired: 'Boot expired',
  plan_posted: 'Plan posted',
  identity_conflict_raised: 'Hardware conflict raised',
  identity_conflict_resolved: 'Hardware conflict resolved',
  bmc_account_intent: 'BMC account requested',
  bmc_account_created: 'BMC account created',
  bmc_account_removed: 'BMC account removed',
};

function SegmentRow({ s }: { s: BenchRunSegmentView }) {
  return (
    <li className="space-y-0.5 border-t border-border pt-2 first:border-t-0 first:pt-0" data-testid="timeline-segment">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Boot {s.segmentIdx + 1}</span>
        <StatusPill tone="neutral">{s.environment === 'winpe' ? 'WinPE' : 'Linux'}</StatusPill>
        {s.foreignVisit === 'uefi' && <StatusPill tone="neutral">UEFI step</StatusPill>}
        <span className="text-muted-foreground">{SEGMENT_REASON[s.reason] ?? 'Other'}</span>
        <StatusPill tone={SEGMENT_STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</StatusPill>
      </div>
      <p className="text-xs text-muted-foreground">
        {segmentStatusText(s)}
        {s.consumedAt ? ` - booted ${formatBenchWhen(s.consumedAt)}` : ` - created ${formatBenchWhen(s.createdAt)}`}
        {s.closedAt ? ` - closed ${formatBenchWhen(s.closedAt)}` : ''}
      </p>
      {s.agentVersion !== null && (
        <p className="text-xs text-muted-foreground">
          Agent reports version <AgentText value={s.agentVersion} />
        </p>
      )}
    </li>
  );
}

function EventRow({ e }: { e: BenchRunEventView }) {
  const known = Object.prototype.hasOwnProperty.call(EVENT_LABEL, e.kind) ? EVENT_LABEL[e.kind] : null;
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="timeline-event">
      <span className="text-muted-foreground">{formatBenchWhen(e.createdAt)}</span>
      {known ? (
        <span>{known}</span>
      ) : (
        <span>
          Other event <AgentText value={e.kind} max={48} />
        </span>
      )}
      {e.reason !== null && <AgentText value={e.reason} max={64} className="text-xs text-muted-foreground" />}
      {e.segmentIdx !== null && <span className="text-xs text-muted-foreground">boot {e.segmentIdx + 1}</span>}
      {e.byYou && <StatusPill tone="neutral">you</StatusPill>}
    </li>
  );
}

function PendingAttachNotice({ run }: { run: BenchRunDetail }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const cancel = useMutation({
    mutationFn: () => cancelBenchAttach(run.id),
    onMutate: () => setMessage(null),
    onError: (err) => {
      const code = (err as { body?: { error?: { code?: string } } } | null)?.body?.error?.code;
      setMessage(
        code === 'none_open'
          ? 'Nothing to cancel: the machine already continued the run, or the approval expired.'
          : code === 'try_again'
            ? 'The system is busy. Try again in a moment.'
            : 'The approval could not be cancelled. Try again.',
      );
    },
    // Success or not, the run is the truth: a settled attach has no pendingAttach any more.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: qk.bench.run(run.id) }),
  });
  if (!run.pendingAttach || run.status !== 'running') return null;
  return (
    <InsightBanner
      tone="neutral"
      lead="Waiting for the machine to continue this run"
      action={
        <Button variant="secondary" size="sm" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
          Cancel approval
        </Button>
      }
    >
      An attach was approved {formatBenchWhen(run.pendingAttach.approvedAt)}
      {run.pendingAttach.byYou ? ' by you' : ''}. The machine continues the run on its next check-in, within {Math.round(BENCH_ATTACH_LANDS_WITHIN_SECONDS / 60)} minutes. Until then it
      can be cancelled.
      {message && (
        <span role="alert" className="mt-1 block text-status-red">
          {message}
        </span>
      )}
    </InsightBanner>
  );
}

export function RunTimeline({ run }: { run: BenchRunDetail }) {
  const segments = run.segments ?? [];
  const events = run.events ?? [];
  // An API that predates SIL-1708 sends neither field and nothing is shown.
  if (segments.length === 0 && events.length === 0 && !run.pendingAttach) return null;

  return (
    <Card className="space-y-3">
      <PanelTitle title="Boots and events" description="Each boot of the machine for this run, and what happened, newest first." />
      <PendingAttachNotice run={run} />
      {segments.length > 0 && (
        <ul className="space-y-2 text-sm" data-testid="timeline-segments">
          {[...segments].sort((a, b) => a.segmentIdx - b.segmentIdx).map((s) => (
            <SegmentRow key={s.segmentIdx} s={s} />
          ))}
        </ul>
      )}
      {events.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Events</p>
          <ul className="space-y-1 text-sm" data-testid="timeline-events">
            {events.map((e) => (
              <EventRow key={e.id} e={e} />
            ))}
          </ul>
          {run.eventsTruncated && <p className="text-xs text-muted-foreground">Showing the newest {events.length} events.</p>}
        </div>
      )}
    </Card>
  );
}
